import { Link } from '@tanstack/react-router';
import { ShoppingBag } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { FeatureErrorBoundary } from '@nucleus-commerce/admin-kit/errors';
import { PageContent, PageHeader } from '@nucleus-commerce/admin-kit/layout';
import { Can } from '@nucleus-commerce/admin-kit/permissions';
import { Button } from '@nucleus-commerce/ui/components/button';

import { useStoreDetail } from '@/features/stores';
import { ReadOnlyBanner } from '@/shared/store/read-only-banner';
import { useStore } from '@/shared/store/store-context';
import { useStoreEditable } from '@/shared/store/use-store-editable';

import { RecoverySettingsCard } from '../components/recovery-settings-card';
import { abandonedCheckoutListSearchSchema } from '../schemas/abandoned-checkout-list-search';

function RecoverySettings() {
  const { t } = useTranslation('abandonedCheckouts');
  const store = useStore();
  const { reason } = useStoreEditable({ permission: 'settings.write' });
  // Prefetched by the route loader.
  const { data: detail } = useStoreDetail(store.id);

  return (
    <PageContent>
      <PageHeader
        title={t('settings.pageTitle')}
        description={t('settings.pageDescription', { store: store.name })}
        backTo={{ to: '/org/stores/$storeId', params: { storeId: store.id } }}
        backLabel={t('settings.back')}
        actions={
          <Can permission="orders.read">
            <Button asChild variant="outline" data-tour="settings.recovery.view-checkouts">
              <Link
                to="/s/$storeId/abandoned-checkouts"
                params={{ storeId: store.id }}
                // The list's search type names every param, so an empty search goes through the schema.
                search={abandonedCheckoutListSearchSchema.parse({})}
              >
                <ShoppingBag aria-hidden />
                {t('settings.viewCheckouts')}
              </Link>
            </Button>
          </Can>
        }
      />
      <ReadOnlyBanner reason={reason} />
      <div className="max-w-2xl">
        {detail ? <RecoverySettingsCard key={detail.updatedAt} store={detail} /> : null}
      </div>
    </PageContent>
  );
}

export function RecoverySettingsPage() {
  return (
    <FeatureErrorBoundary name="abandonedCheckouts.settings">
      <RecoverySettings />
    </FeatureErrorBoundary>
  );
}
